import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, Plus, Building2, Users, FileText, TrendingUp,
  Clock, AlertTriangle, CheckCircle, Shield, UserCheck, UserX,
  BarChart3, Settings, ChevronDown, ChevronUp, Search, Download,
  Eye, Ban, RotateCcw, CreditCard, LogOut, Wrench, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RecetasMetricsPanel from "@/components/RecetasMetricsPanel";
import PilotMetricsPanel from "@/components/PilotMetricsPanel";
import { AdminDoctorLinks } from "@/components/AdminDoctorLinks";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// KPI Card component
const KpiCard = ({ icon: Icon, label, value, sub, color = "text-[#1d366b]", bgColor = "bg-blue-50" }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-sm transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</span>
      <div className={`h-8 w-8 ${bgColor} rounded-lg flex items-center justify-center`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value ?? "—"}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

// Simple bar chart component
const SimpleBar = ({ label, value, max, color = "bg-[#1d366b]" }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-500 w-36 truncate">{label}</span>
      <div className="flex-1 h-6 bg-slate-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color} rounded-sm transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
      <span className="text-sm font-semibold text-slate-700 w-8 text-right">{value}</span>
    </div>
  );
};

const AdminPanel = ({ user }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctorStats, setDoctorStats] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [redFlagLogs, setRedFlagLogs] = useState([]);
  const [consults, setConsults] = useState([]);
  const [doctorRequests, setDoctorRequests] = useState([]);
  const [consultStatusFilter, setConsultStatusFilter] = useState("all");
  const [consultInstFilter, setConsultInstFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");

  // Forms
  const [showInstForm, setShowInstForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [institutionForm, setInstitutionForm] = useState({ name: "", allowed_email_domains: "", default_sla_hours: 48 });
  const [userForm, setUserForm] = useState({ name: "", email: "", role: "MEDICO", license_number: "", institution_id: "" });

  // Filters
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [auditSearch, setAuditSearch] = useState("");

  // Patient reset tool
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const token = localStorage.getItem('auth_token');

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/');
    toast.success("Sesión cerrada");
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      const [analyticsRes, instRes, doctorsRes, auditRes, redFlagsRes, consultsRes, dInvitesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics`, { headers }),
        fetch(`${API_URL}/api/institutions`),
        fetch(`${API_URL}/api/admin/doctors`, { headers }),
        fetch(`${API_URL}/api/audit`, { headers }),
        fetch(`${API_URL}/api/audit?action=red_flag_triggered&limit=50`, { headers }),
        fetch(`${API_URL}/api/admin/consults?limit=1000`, { headers }),
        fetch(`${API_URL}/api/admin/doctor-invite-requests?limit=200`, { headers })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (instRes.ok) {
        const instData = await instRes.json();
        setInstitutions(instData);
        if (instData.length > 0 && !userForm.institution_id) {
          setUserForm(prev => ({ ...prev, institution_id: instData[0].id }));
        }
      }
      if (doctorsRes.ok) {
        const dData = await doctorsRes.json();
        setDoctors(dData.doctors || []);
        setDoctorStats(dData.stats || {});
      }
      if (auditRes.ok) setAuditLogs(await auditRes.json());
      if (redFlagsRes.ok) setRedFlagLogs(await redFlagsRes.json());
      if (consultsRes.ok) setConsults(await consultsRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/institutions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...institutionForm, allowed_email_domains: institutionForm.allowed_email_domains.split(',').map(d => d.trim()) })
      });
      if (response.ok) {
        toast.success("Institución creada");
        setInstitutionForm({ name: "", allowed_email_domains: "", default_sla_hours: 48 });
        setShowInstForm(false);
        loadData();
      } else toast.error("Error al crear institución");
    } catch { toast.error("Error de conexión"); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...userForm, license_number: userForm.license_number || null })
      });
      if (response.ok) {
        toast.success("Usuario creado");
        setUserForm({ name: "", email: "", role: "MEDICO", license_number: "", institution_id: institutions[0]?.id || "" });
        setShowUserForm(false);
        loadData();
      } else toast.error("Error al crear usuario");
    } catch { toast.error("Error de conexión"); }
  };

  const handleDoctorAction = async (doctorId, action) => {
    try {
      let endpoint = "";
      let body = {};
      if (action === "validate") {
        endpoint = `${API_URL}/api/admin/doctors/${doctorId}/validate`;
        body = { action: "approve" };
      } else if (action === "suspend") {
        endpoint = `${API_URL}/api/admin/doctors/${doctorId}/suspend`;
        body = { reason: "Suspendido por administrador" };
      } else if (action === "reactivate") {
        endpoint = `${API_URL}/api/admin/doctors/${doctorId}/reactivate`;
      } else if (action === "resend-activation") {
        endpoint = `${API_URL}/api/admin/doctors/${doctorId}/resend-activation`;
      }
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (resp.ok) {
        toast.success(`Acción completada`);
        loadData();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Error");
      }
    } catch { toast.error("Error de conexión"); }
  };

  const exportAuditCSV = () => {
    const headers = "Fecha,Acción,Usuario,Consulta\n";
    const rows = auditLogs.map(l =>
      `"${new Date(l.timestamp).toLocaleString('es-AR')}","${l.action}","${l.user_id || ''}","${l.consult_id || ''}"`
    ).join("\n");
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auditoria_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV descargado");
  };

  // CSV-safe escape: wrap in quotes and double-up internal quotes
  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  };

  const handleResetPatientData = async (e) => {
    e.preventDefault();
    const email = (resetEmail || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Ingresá un email válido");
      return;
    }
    if (!window.confirm(`Esto eliminará los datos de reingreso (DNI ↔ email) del paciente ${email}.\nLas consultas previas se conservan en auditoría.\n\n¿Continuar?`)) {
      return;
    }
    setResetLoading(true);
    setResetResult(null);
    try {
      const url = `${API_URL}/api/admin/reset-patient-data?email=${encodeURIComponent(email)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setResetResult(data);
        toast.success("Datos del paciente reseteados");
      } else {
        toast.error(data.detail || "Error al resetear");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setResetLoading(false);
    }
  };

  const exportConsultsCSV = () => {
    const filtered = filteredConsults;
    if (!filtered.length) {
      toast.error("No hay consultas para exportar");
      return;
    }
    const cols = [
      ["ID", "id"],
      ["Estado", "status"],
      ["Categoría", "category"],
      ["Obra social", "obra_social"],
      ["Médico", "doctor_name"],
      ["Email médico", "doctor_email"],
      ["Institución", "institution_name"],
      ["Creada", "created_at"],
      ["SLA deadline", "sla_deadline"],
      ["Respondida en", "responded_at"],
      ["Escalada", "escalated"],
      ["Monto", "amount"],
    ];
    const header = cols.map(c => csvEscape(c[0])).join(",");
    const rows = filtered.map(c =>
      cols.map(col => {
        let v = c[col[1]];
        if (col[1] === "escalated") v = v ? "Sí" : "No";
        if (["created_at", "sla_deadline", "responded_at"].includes(col[1]) && v) {
          v = new Date(v).toLocaleString('es-AR');
        }
        return csvEscape(v);
      }).join(",")
    ).join("\n");
    const blob = new Blob(["\ufeff" + header + "\n" + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consultas_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV descargado (${filtered.length} consultas)`);
  };

  const filteredDoctors = doctors.filter(d => {
    if (doctorFilter === "all") return true;
    return d.doctor_status === doctorFilter;
  });

  const filteredAudit = auditLogs.filter(l => {
    if (!auditSearch) return true;
    const search = auditSearch.toLowerCase();
    return (l.action || '').toLowerCase().includes(search) ||
           (l.user_id || '').toLowerCase().includes(search) ||
           (l.consult_id || '').toLowerCase().includes(search);
  });

  const filteredConsults = consults.filter(c => {
    if (consultStatusFilter !== "all" && c.status !== consultStatusFilter) return false;
    if (consultInstFilter !== "all" && c.institution_name !== consultInstFilter) return false;
    return true;
  });

  const actionLabels = {
    "consult_created_direct": "Consulta creada",
    "consult_responded": "Consulta respondida",
    "consult_escalated": "Consulta escalada",
    "consult_rejected_triage": "Rechazada (triaje)",
    "consult_blocked_urgent": "Bloqueada (urgente)",
    "doctor_registered": "Médico registrado",
    "doctor_validated": "Médico validado",
    "doctor_suspended": "Médico suspendido",
    "info_requested": "Info solicitada",
    "info_received": "Info recibida",
    "red_flag_triggered": "Triaje urgente",
  };

  const actionColor = (action) => {
    if (action?.includes("respond")) return "bg-green-100 text-green-700";
    if (action?.includes("escalat")) return "bg-amber-100 text-amber-700";
    if (action?.includes("block") || action?.includes("reject") || action?.includes("suspend") || action === "red_flag_triggered") return "bg-red-100 text-red-700";
    if (action?.includes("creat") || action?.includes("register")) return "bg-blue-100 text-blue-700";
    if (action?.includes("validat")) return "bg-emerald-100 text-emerald-700";
    return "bg-slate-100 text-slate-600";
  };

  const consultStatusColor = (status) => {
    switch (status) {
      case "Responded": return "bg-green-100 text-green-700";
      case "Paid": case "InReview": return "bg-blue-100 text-blue-700";
      case "ESCALATED": return "bg-amber-100 text-amber-700";
      case "AWAITING_INFO": case "INFO_RECEIVED": return "bg-purple-100 text-purple-700";
      case "UrgentBlocked": case "RejectedByTriage": return "bg-red-100 text-red-700";
      case "CLOSED_TIMEOUT": return "bg-slate-200 text-slate-600";
      case "PendingPayment": return "bg-yellow-100 text-yellow-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const consultStatusLabels = {
    Paid: "Pagada", InReview: "En revisión", Responded: "Respondida",
    ESCALATED: "Escalada", AWAITING_INFO: "Esperando info", INFO_RECEIVED: "Info recibida",
    PendingPayment: "Pago pendiente", CLOSED_TIMEOUT: "Cerrada (timeout)",
    UrgentBlocked: "Bloqueada urgente", RejectedByTriage: "Rechazada (triaje)"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d366b] mx-auto" />
          <p className="mt-4 text-slate-400">Cargando panel...</p>
        </div>
      </div>
    );
  }

  const a = analytics || {};
  const sc = a.status_counts || {};
  const catDist = a.category_distribution || {};
  const maxCat = Math.max(...Object.values(catDist), 1);
  const escReasons = a.escalation_reasons || {};
  const maxEsc = Math.max(...Object.values(escReasons), 1);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img src="/logo-fleni.png" alt="Fleni" className="h-8 sm:h-10 object-contain" />
              <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded">Admin</span>
            </div>
            <div className="flex items-center gap-3">
              {user?.email && (
                <span className="hidden sm:inline text-xs text-slate-500" data-testid="admin-user-email">
                  {user.email}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="text-slate-700 hover:text-red-600 hover:border-red-200"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="dashboard" data-testid="dashboard-tab">
              <BarChart3 className="h-4 w-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="pilot" data-testid="pilot-tab">
              <TrendingUp className="h-4 w-4 mr-2" /> Piloto
            </TabsTrigger>
            <TabsTrigger value="professionals" data-testid="professionals-tab">
              <Users className="h-4 w-4 mr-2" /> Profesionales
            </TabsTrigger>
            <TabsTrigger value="institutions" data-testid="institutions-tab">
              <Building2 className="h-4 w-4 mr-2" /> Instituciones
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-tab">
              <Shield className="h-4 w-4 mr-2" /> Auditoría
            </TabsTrigger>
            <TabsTrigger value="consults" data-testid="consults-tab">
              <FileText className="h-4 w-4 mr-2" /> Consultas
            </TabsTrigger>
            <TabsTrigger value="recetas" data-testid="recetas-tab">
              <FileText className="h-4 w-4 mr-2" /> Recetas
            </TabsTrigger>
            <TabsTrigger value="red-flags" data-testid="red-flags-tab">
              <AlertTriangle className="h-4 w-4 mr-2" /> Triaje urgente
            </TabsTrigger>
            <TabsTrigger value="tools" data-testid="tools-tab">
              <Wrench className="h-4 w-4 mr-2" /> Herramientas
            </TabsTrigger>
          </TabsList>

          {/* ===== DASHBOARD TAB ===== */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <KpiCard icon={Activity} label="Consultas totales" value={a.total_consults} />
              <KpiCard icon={Users} label="Profesionales activos" value={a.active_doctors} sub={`${a.total_doctors || 0} registrados`} color="text-emerald-600" bgColor="bg-emerald-50" />
              <KpiCard icon={Clock} label="Tiempo promedio" value={a.avg_response_hours ? `${a.avg_response_hours}h` : "—"} sub="de respuesta" />
              <KpiCard icon={CheckCircle} label="SLA cumplido" value={a.sla_compliance != null ? `${a.sla_compliance}%` : "—"} color="text-emerald-600" bgColor="bg-emerald-50" />
              <KpiCard icon={AlertTriangle} label="Escaladas" value={sc.ESCALATED || 0} color="text-amber-600" bgColor="bg-amber-50" />
              <KpiCard icon={Clock} label="Vencidas" value={a.overdue || 0} color="text-red-600" bgColor="bg-red-50" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Status breakdown */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Estado de consultas</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Pendientes", value: sc.Paid || 0, color: "text-amber-600", bg: "bg-amber-50" },
                    { label: "Respondidas", value: sc.Responded || 0, color: "text-emerald-600", bg: "bg-emerald-50" },
                    { label: "Escaladas", value: sc.ESCALATED || 0, color: "text-orange-600", bg: "bg-orange-50" },
                    { label: "Esperando info", value: sc.AWAITING_INFO || 0, color: "text-blue-600", bg: "bg-blue-50" },
                    { label: "Info recibida", value: sc.INFO_RECEIVED || 0, color: "text-violet-600", bg: "bg-violet-50" },
                    { label: "Timeout", value: sc.CLOSED_TIMEOUT || 0, color: "text-slate-600", bg: "bg-slate-50" },
                  ].map(s => (
                    <div key={s.label} className={`${s.bg} rounded-lg p-3 flex items-center justify-between`}>
                      <span className="text-xs text-slate-500">{s.label}</span>
                      <span className={`text-lg font-bold ${s.color}`}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category distribution */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Consultas por categoría</h3>
                <div className="space-y-3">
                  {Object.entries(catDist).map(([cat, count]) => (
                    <SimpleBar key={cat} label={cat} value={count} max={maxCat} />
                  ))}
                  {Object.keys(catDist).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sin datos</p>}
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Doctor performance */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Rendimiento por profesional</h3>
                {(a.doctor_performance || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Sin datos de rendimiento</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="doctor-performance-table">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-2 text-xs text-slate-400 font-medium">Profesional</th>
                          <th className="text-center py-2 text-xs text-slate-400 font-medium">Respondidas</th>
                          <th className="text-center py-2 text-xs text-slate-400 font-medium">Prom. resp.</th>
                          <th className="text-center py-2 text-xs text-slate-400 font-medium">Escaladas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(a.doctor_performance || []).map(dp => (
                          <tr key={dp.doctor_id} className="border-b border-slate-50">
                            <td className="py-2.5">
                              <p className="font-medium text-slate-700">{dp.name}</p>
                              <p className="text-xs text-slate-400">{dp.email}</p>
                            </td>
                            <td className="text-center font-semibold text-slate-700">{dp.total_responded}</td>
                            <td className="text-center">
                              {dp.avg_response_time != null ? (
                                <span className={dp.avg_response_time <= 48 ? "text-emerald-600" : "text-amber-600"}>
                                  {dp.avg_response_time}h
                                </span>
                              ) : "—"}
                            </td>
                            <td className="text-center text-amber-600">{dp.escalated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Escalation reasons */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Motivos de escalamiento</h3>
                <div className="space-y-3">
                  {Object.entries(escReasons).map(([reason, count]) => (
                    <SimpleBar key={reason} label={reason} value={count} max={maxEsc} color="bg-amber-500" />
                  ))}
                  {Object.keys(escReasons).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sin escalamientos</p>}
                </div>
              </div>
            </div>

            {/* Recent activity */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Actividad reciente</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(a.recent_activity || []).slice(0, 10).map((log, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${actionColor(log.action)}`}>
                      {actionLabels[log.action] || log.action}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto">
                      {new Date(log.timestamp).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
                {(a.recent_activity || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sin actividad reciente</p>}
              </div>
            </div>
          </TabsContent>

          {/* ===== PROFESSIONALS TAB ===== */}
          <TabsContent value="professionals" className="space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-[#1d366b]">Profesionales</h2>
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded">{doctors.length} total</span>
              </div>
              <div className="flex items-center gap-3">
                <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid="doctor-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ACTIVE">Activos</SelectItem>
                    <SelectItem value="PENDING_REVIEW">Pendientes</SelectItem>
                    <SelectItem value="SUSPENDED">Suspendidos</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => setShowUserForm(!showUserForm)} data-testid="toggle-user-form-btn">
                  <Plus className="h-4 w-4 mr-1" /> Nuevo
                </Button>
              </div>
            </div>

            {/* Status summary pills */}
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Activos", value: doctorStats.active || 0, color: "bg-emerald-100 text-emerald-700" },
                { label: "Pendientes", value: doctorStats.pending_review || 0, color: "bg-amber-100 text-amber-700" },
                { label: "Perfil incompleto", value: doctorStats.pending_profile || 0, color: "bg-blue-100 text-blue-700" },
                { label: "Suspendidos", value: doctorStats.suspended || 0, color: "bg-red-100 text-red-700" },
              ].map(s => s.value > 0 && (
                <span key={s.label} className={`text-xs px-3 py-1 rounded-full ${s.color}`}>
                  {s.label}: {s.value}
                </span>
              ))}
            </div>

            {/* Create user form (collapsible) */}
            {showUserForm && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Crear nuevo usuario</h3>
                <form onSubmit={handleCreateUser} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="create-user-form">
                  <div>
                    <Label className="text-xs">Nombre *</Label>
                    <Input data-testid="user-name-input" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Dr. Juan Pérez" required className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input data-testid="user-email-input" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="doctor@fleni.org.ar" required className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Rol *</Label>
                    <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                      <SelectTrigger className="h-9 mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ADMIN">Admin</SelectItem>
                        <SelectItem value="MEDICO">Médico</SelectItem>
                        <SelectItem value="STAFF">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Matrícula</Label>
                    <Input data-testid="user-license-input" value={userForm.license_number} onChange={(e) => setUserForm({ ...userForm, license_number: e.target.value })} placeholder="MN 12345" className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Institución *</Label>
                    <Select value={userForm.institution_id} onValueChange={(v) => setUserForm({ ...userForm, institution_id: v })}>
                      <SelectTrigger className="h-9 mt-1" data-testid="user-institution-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {institutions.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="w-full h-9" data-testid="create-user-btn">
                      <Plus className="h-4 w-4 mr-1" /> Crear
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Doctors table */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="doctors-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Profesional</th>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Email</th>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Matrícula</th>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Links</th>
                      <th className="text-center p-3 text-xs font-medium text-slate-400">Estado</th>
                      <th className="text-center p-3 text-xs font-medium text-slate-400">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDoctors.map(d => (
                      <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                        <td className="p-3 font-medium text-slate-700">{d.name || "—"}</td>
                        <td className="p-3 text-slate-500">{d.email}</td>
                        <td className="p-3 text-slate-500">{d.license_number || "—"}</td>
                        <td className="p-3"><AdminDoctorLinks doctor={d} /></td>
                        <td className="p-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            d.doctor_status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" :
                            d.doctor_status === "PENDING_REVIEW" ? "bg-amber-100 text-amber-700" :
                            d.doctor_status === "SUSPENDED" ? "bg-red-100 text-red-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {d.doctor_status || "—"}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {d.doctor_status === "PENDING_REVIEW" && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:text-emerald-700" onClick={() => handleDoctorAction(d.id, "validate")} data-testid={`validate-${d.id}`}>
                                <UserCheck className="h-3 w-3 mr-1" /> Aprobar
                              </Button>
                            )}
                            {(d.doctor_status === "PENDING_PROFILE" || d.doctor_status === "PENDING_EMAIL") && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-600 hover:text-slate-800" onClick={() => handleDoctorAction(d.id, "resend-activation")} data-testid={`resend-activation-${d.id}`}>
                                  <Mail className="h-3 w-3 mr-1" /> Reenviar enlace
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:text-emerald-700" onClick={() => { if (window.confirm("¿Activar este profesional sin matrícula cargada? Podés hacerlo igual para el piloto, pero el perfil quedará incompleto.")) handleDoctorAction(d.id, "validate"); }} data-testid={`activate-${d.id}`}>
                                  <UserCheck className="h-3 w-3 mr-1" /> Activar
                                </Button>
                              </>
                            )}
                            {d.doctor_status === "ACTIVE" && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-600" onClick={() => handleDoctorAction(d.id, "suspend")} data-testid={`suspend-${d.id}`}>
                                <Ban className="h-3 w-3 mr-1" /> Suspender
                              </Button>
                            )}
                            {d.doctor_status === "SUSPENDED" && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600 hover:text-blue-700" onClick={() => handleDoctorAction(d.id, "reactivate")} data-testid={`reactivate-${d.id}`}>
                                <RotateCcw className="h-3 w-3 mr-1" /> Reactivar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredDoctors.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-slate-400">No hay profesionales con este filtro</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ===== INSTITUTIONS TAB ===== */}
          <TabsContent value="institutions" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1d366b]">Instituciones</h2>
              <Button size="sm" onClick={() => setShowInstForm(!showInstForm)} data-testid="toggle-inst-form-btn">
                <Plus className="h-4 w-4 mr-1" /> Nueva
              </Button>
            </div>

            {showInstForm && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <form onSubmit={handleCreateInstitution} className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="create-institution-form">
                  <div>
                    <Label className="text-xs">Nombre *</Label>
                    <Input data-testid="institution-name-input" value={institutionForm.name} onChange={(e) => setInstitutionForm({ ...institutionForm, name: e.target.value })} placeholder="Nombre institución" required className="h-9 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Dominios de email *</Label>
                    <Input data-testid="institution-domains-input" value={institutionForm.allowed_email_domains} onChange={(e) => setInstitutionForm({ ...institutionForm, allowed_email_domains: e.target.value })} placeholder="fleni.org.ar, gmail.com" required className="h-9 mt-1" />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">SLA (horas)</Label>
                      <Input data-testid="institution-sla-input" type="number" value={institutionForm.default_sla_hours} onChange={(e) => setInstitutionForm({ ...institutionForm, default_sla_hours: parseInt(e.target.value) })} className="h-9 mt-1" />
                    </div>
                    <Button type="submit" className="h-9" data-testid="create-institution-btn">
                      <Plus className="h-4 w-4 mr-1" /> Crear
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {institutions.map(inst => (
                <div key={inst.id} className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-[#1d366b]">{inst.name}</h3>
                    {inst.is_active !== false && <span className="text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full">Activa</span>}
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-500">
                    <p>Dominios: {inst.allowed_email_domains?.join(', ')}</p>
                    <p>SLA: {inst.default_sla_hours}h</p>
                    {inst.slug && <p>Slug: {inst.slug}</p>}
                  </div>
                </div>
              ))}
              {institutions.length === 0 && <p className="text-sm text-slate-400 col-span-3 text-center py-8">No hay instituciones</p>}
            </div>
          </TabsContent>

          {/* ===== AUDIT TAB ===== */}
          <TabsContent value="audit" className="space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-[#1d366b]">Registros de auditoría</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                  <Input
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    placeholder="Buscar acción, usuario..."
                    className="pl-9 h-9 w-64"
                    data-testid="audit-search-input"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={exportAuditCSV} data-testid="export-audit-btn">
                  <Download className="h-4 w-4 mr-1" /> CSV
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-logs-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Fecha</th>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Acción</th>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Usuario</th>
                      <th className="text-left p-3 text-xs font-medium text-slate-400">Consulta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAudit.slice(0, 50).map((log, idx) => (
                      <tr key={idx} className="border-t border-slate-50 hover:bg-slate-50/50">
                        <td className="p-3 text-xs text-slate-400 whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${actionColor(log.action)}`}>
                            {actionLabels[log.action] || log.action}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-slate-500">{log.user_id ? `${log.user_id.substring(0, 8)}...` : '—'}</td>
                        <td className="p-3 text-xs text-slate-500">{log.consult_id ? `${log.consult_id.substring(0, 8)}...` : '—'}</td>
                      </tr>
                    ))}
                    {filteredAudit.length === 0 && (
                      <tr><td colSpan={4} className="p-8 text-center text-slate-400">Sin registros</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filteredAudit.length > 50 && (
                <div className="p-3 text-center text-xs text-slate-400 border-t border-slate-100">
                  Mostrando 50 de {filteredAudit.length} registros
                </div>
              )}
            </div>
          </TabsContent>

          {/* ===== CONSULTAS TAB ===== */}
          <TabsContent value="consults" className="space-y-6">
            {(() => {
              const uniqueInstitutions = Array.from(new Set(consults.map(c => c.institution_name).filter(Boolean))).sort();
              const statusOptions = Object.keys(consultStatusLabels);
              return (
                <>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-[#1d366b] flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Todas las consultas
                      </h2>
                      <p className="text-xs text-slate-400 mt-1">
                        Mostrando {filteredConsults.length} de {consults.length} consultas.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={consultStatusFilter}
                        onChange={(e) => setConsultStatusFilter(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white"
                        data-testid="consults-status-filter"
                      >
                        <option value="all">Todos los estados</option>
                        {statusOptions.map(s => (
                          <option key={s} value={s}>{consultStatusLabels[s]}</option>
                        ))}
                      </select>
                      <select
                        value={consultInstFilter}
                        onChange={(e) => setConsultInstFilter(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white"
                        data-testid="consults-institution-filter"
                      >
                        <option value="all">Todas las instituciones</option>
                        {uniqueInstitutions.map(i => (
                          <option key={i} value={i}>{i}</option>
                        ))}
                      </select>
                      <Button variant="outline" size="sm" onClick={exportConsultsCSV} data-testid="export-consults-btn">
                        <Download className="h-4 w-4 mr-1" /> CSV
                      </Button>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="consults-table">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 text-xs font-medium text-slate-400">Creada</th>
                            <th className="text-left p-3 text-xs font-medium text-slate-400">Estado</th>
                            <th className="text-left p-3 text-xs font-medium text-slate-400">Médico</th>
                            <th className="text-left p-3 text-xs font-medium text-slate-400 hidden lg:table-cell">Institución</th>
                            <th className="text-left p-3 text-xs font-medium text-slate-400 hidden lg:table-cell">Categoría</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredConsults.slice(0, 100).map((c) => (
                            <tr key={c.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                              <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                                {c.created_at ? new Date(c.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : "—"}
                              </td>
                              <td className="p-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${consultStatusColor(c.status)}`}>
                                  {consultStatusLabels[c.status] || c.status}
                                </span>
                              </td>
                              <td className="p-3 text-xs text-slate-500">{c.doctor_name || "—"}</td>
                              <td className="p-3 text-xs text-slate-500 hidden lg:table-cell">{c.institution_name || "—"}</td>
                              <td className="p-3 text-xs text-slate-500 hidden lg:table-cell">{c.category || "—"}</td>
                            </tr>
                          ))}
                          {filteredConsults.length === 0 && (
                            <tr><td colSpan={5} className="p-8 text-center text-slate-400" data-testid="consults-empty">
                              Sin consultas que cumplan los filtros.
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {filteredConsults.length > 100 && (
                      <div className="p-3 text-center text-xs text-slate-400 border-t border-slate-100">
                        Mostrando 100 de {filteredConsults.length} consultas filtradas. Exportá a CSV para verlas todas.
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </TabsContent>

          {/* ===== RECETAS TAB ===== */}
          <TabsContent value="recetas" className="space-y-6">
            <RecetasMetricsPanel />
          </TabsContent>

          {/* ===== PILOTO TAB ===== */}
          <TabsContent value="pilot" className="space-y-6">
            <PilotMetricsPanel />
          </TabsContent>

          {/* ===== RED FLAGS / TRIAJE URGENTE TAB ===== */}
          <TabsContent value="red-flags" className="space-y-6">
            {(() => {
              const redFlagItems = redFlagLogs.slice(0, 50);

              // Aggregate count by symptom for a quick distribution glance
              const bySymptom = {};
              redFlagItems.forEach(l => {
                const key = l.metadata?.red_flag_label || l.metadata?.red_flag_key || "Sin dato";
                bySymptom[key] = (bySymptom[key] || 0) + 1;
              });
              const topSymptoms = Object.entries(bySymptom)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5);

              return (
                <>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-[#1d366b] flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        Activaciones de triaje urgente
                      </h2>
                      <p className="text-xs text-slate-400 mt-1">
                        Pacientes que reportaron síntomas de alarma y fueron derivados a atención presencial.
                        Mostrando los últimos {redFlagItems.length} eventos.
                      </p>
                    </div>
                  </div>

                  {/* Symptom distribution */}
                  {topSymptoms.length > 0 && (
                    <div className="bg-white rounded-lg border border-slate-200 p-4">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                        Síntomas más frecuentes
                      </p>
                      <div className="space-y-2">
                        {topSymptoms.map(([symptom, count]) => {
                          const pct = redFlagItems.length ? Math.round((count / redFlagItems.length) * 100) : 0;
                          return (
                            <div key={symptom} className="flex items-center gap-3" data-testid={`red-flag-stat-${count}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-700 truncate pr-2">{symptom}</span>
                                  <span className="text-slate-500 text-xs flex-shrink-0">{count} · {pct}%</span>
                                </div>
                                <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-red-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Detail table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="red-flags-table">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 text-xs font-medium text-slate-400">Fecha / Hora</th>
                            <th className="text-left p-3 text-xs font-medium text-slate-400">Síntoma reportado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {redFlagItems.map((log, idx) => {
                            const md = log.metadata || {};
                            return (
                              <tr key={idx} className="border-t border-slate-50 hover:bg-slate-50/50">
                                <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                                  {new Date(log.timestamp).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td className="p-3 text-sm text-slate-800">
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-red-500" />
                                    {md.red_flag_label || md.red_flag_key || "—"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {redFlagItems.length === 0 && (
                            <tr><td colSpan={2} className="p-8 text-center text-slate-400" data-testid="red-flags-empty">
                              Aún no hay activaciones de triaje urgente registradas.
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}
          </TabsContent>

          {/* ===== TOOLS TAB ===== */}
          <TabsContent value="tools" className="space-y-6">
            <div className="bg-white rounded-lg border border-slate-200 p-6 max-w-2xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Resetear datos de reingreso del paciente</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Elimina la asociación <strong>DNI ↔ email</strong> de un paciente para que pueda registrarse nuevamente con un DNI distinto.
                    Útil cuando un paciente cargó un DNI incorrecto.
                    <span className="block mt-1 text-xs text-emerald-700">✓ Las consultas previas se conservan en auditoría.</span>
                  </p>
                </div>
              </div>

              <form onSubmit={handleResetPatientData} className="space-y-3">
                <div>
                  <Label htmlFor="reset-email">Email del paciente</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="paciente@ejemplo.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    data-testid="reset-email-input"
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={resetLoading || !resetEmail.trim()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="reset-patient-data-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {resetLoading ? "Reseteando..." : "Resetear datos del paciente"}
                </Button>
              </form>

              {resetResult && (
                <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="reset-result">
                  <p className="text-sm font-semibold text-emerald-900 mb-2">✓ Reset completado para {resetResult.email}</p>
                  <ul className="text-sm text-emerald-800 space-y-1">
                    <li>• Registros DNI↔email eliminados: <strong>{resetResult.deleted_patient_doctors}</strong></li>
                    <li>• Magic links eliminados: <strong>{resetResult.deleted_patient_magic_links}</strong></li>
                    <li>• Consultas previas conservadas (auditoría): <strong>{resetResult.consults_preserved}</strong></li>
                  </ul>
                  <p className="text-xs text-emerald-700 mt-2">
                    El paciente ya puede iniciar una nueva consulta desde el Smart Link y cargar su DNI correcto.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
